import React, { useEffect, useState } from 'react';
import { Box, Skeleton, SxProps, Theme, Typography } from '@mui/material';
import ArticleIcon from '@mui/icons-material/Article';
import { getDisplayableImageUrl } from '../utils/mediaUrl';

type ArticleImageVariant = 'card' | 'related' | 'hero';

const VARIANT_STYLES: Record<ArticleImageVariant, SxProps<Theme>> = {
  card: {
    width: '100%',
    height: 180,
    flexShrink: 0,
  },
  related: {
    width: '100%',
    height: '100%',
  },
  hero: {
    width: '100%',
    maxHeight: 420,
    height: { xs: 220, sm: 320, md: 420 },
    mb: 4,
    borderRadius: 3,
  },
};

interface ArticleImageProps {
  imageUrl?: string | null;
  alt: string;
  variant?: ArticleImageVariant;
  sx?: SxProps<Theme>;
}

const ArticleImage: React.FC<ArticleImageProps> = ({
  imageUrl,
  alt,
  variant = 'card',
  sx,
}) => {
  const [loaded, setLoaded] = useState(false);
  const [failed, setFailed] = useState(false);
  const displayUrl = getDisplayableImageUrl(imageUrl);
  const isCard = variant === 'card';

  useEffect(() => {
    setLoaded(false);
    setFailed(false);
  }, [displayUrl]);

  const handleLoad = () => {
    setLoaded(true);
    setFailed(false);
  };

  const handleError = () => {
    setLoaded(false);
    setFailed(true);
  };

  if (!displayUrl || failed) {
    return (
      <Box
        sx={[
          {
            position: 'relative',
            overflow: 'hidden',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            background: 'linear-gradient(135deg, rgba(17, 94, 89, 0.06) 0%, rgba(53, 188, 179, 0.06) 100%)',
            borderBottom: '1px solid rgba(53, 188, 179, 0.06)',
          },
          VARIANT_STYLES[variant],
          ...(Array.isArray(sx) ? sx : sx ? [sx] : []),
        ]}
      >
        <ArticleIcon sx={{ fontSize: 44, color: 'rgba(42, 168, 153, 0.35)' }} />
      </Box>
    );
  }

  // Detail pages: preload image without showing a skeleton placeholder.
  // We removed this block to guarantee immediate image rendering on single details layout.

  const isLoading = isCard && !loaded;

  return (
    <Box
      sx={[
        {
          position: 'relative',
          overflow: 'hidden',
          display: 'block',
          bgcolor: isCard ? 'rgba(53, 188, 179, 0.06)' : undefined,
        },
        VARIANT_STYLES[variant],
        ...(Array.isArray(sx) ? sx : sx ? [sx] : []),
      ]}
    >
      {isLoading && (
        <>
          <Skeleton
            variant="rectangular"
            animation="wave"
            sx={{
              position: 'absolute',
              inset: 0,
              width: '100%',
              height: '100%',
              transform: 'none',
            }}
          />
          <Box
            sx={{
              position: 'absolute',
              inset: 0,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              pointerEvents: 'none',
            }}
          >
            <Typography
              component="span"
              aria-hidden
              sx={{ fontSize: '2rem', opacity: 0.35, lineHeight: 1 }}
            >
              <ArticleIcon sx={{ fontSize: 32, opacity: 0.35 }} />
            </Typography>
          </Box>
        </>
      )}

      <Box
        component="img"
        src={displayUrl}
        alt={alt}
        loading="lazy"
        onLoad={handleLoad}
        onError={handleError}
        sx={{
          width: '100%',
          height: '100%',
          objectFit: 'cover',
          display: 'block',
          opacity: isCard ? (loaded ? 1 : 0) : 1,
          transition: 'opacity 0.25s ease-in-out',
        }}
      />
    </Box>
  );
};

export default ArticleImage;
